// Single source of truth for order status filter options and labels — mirrors
// the backend Order.Status choices (backend/orders/models.py). StatusBadge,
// AdminOrderStatusPill and the engineer Orders inline filter all import from
// here rather than keeping their own copies (see DEV-51079).

export interface OrderStatusOption {
  value: string
  label: string
}

// Backend status value → user-facing label. `received_by_supplier` and
// `rejected` never persist as `Order.status` (status_mapper.py folds them into
// awaiting_supplier and fulfillment_declined respectively), but are listed
// defensively so any surface that ever renders those raw strings gets the
// same label as the status they collapse into.
export const ORDER_STATUS_LABELS: Record<string, string> = {
  awaiting_approval: 'Awaiting approval',
  approved: 'Approved',
  started_processing: 'Submitting',
  awaiting_supplier: 'Sent to supplier',
  received_by_supplier: 'With supplier',
  processing: 'With supplier',
  ready_to_collect: 'Ready to collect',
  out_for_delivery: 'Out for delivery',
  completed: 'Completed',
  submission_failed: 'Not sent (retry)',
  internal_decline: 'Declined by approver',
  rejected: 'Declined by approver',
  fulfillment_declined: 'Declined by supplier',
  supplier_cancelled: 'Declined by supplier',
  withdrawn: 'Withdrawn',
  cancelled: 'Cancelled',
}

// Resolve a backend status string to its display label. Falls back to a
// prettified snake-case → sentence-case form so a brand-new backend status
// still renders something sensible before this map is updated.
export function getStatusLabel(status: string | null | undefined): string {
  if (!status) return 'Unknown'
  const known = ORDER_STATUS_LABELS[status]
  if (known) return known
  const spaced = status.replace(/_/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

// Filter dropdown rows — one entry per unique label. Where two backend
// statuses share a label (see STATUS_FILTER_GROUPS below) only the canonical
// value appears here; selecting that row is then expanded by the backend
// (or by getStatusFilterMatches on the engineer client-side filter) to match
// every backend value in the group.
//
// Order matches the lifecycle progression.
const FILTER_DROPDOWN_VALUES = [
  'awaiting_approval',
  'approved',
  'started_processing',   // "Submitting" — retry loop before supplier acks
  'awaiting_supplier',    // "Sent to supplier"
  'processing',           // "With supplier"
  'ready_to_collect',
  'out_for_delivery',
  'completed',
  'submission_failed',
  'internal_decline',     // "Declined by approver"
  'fulfillment_declined', // "Declined by supplier" — also covers supplier_cancelled
  'withdrawn',
  'cancelled',
]

export const ORDER_STATUS_OPTIONS: OrderStatusOption[] = FILTER_DROPDOWN_VALUES.map(
  (value) => ({ value, label: ORDER_STATUS_LABELS[value] })
)

// Merged filter groups — a single dropdown option matches every backend
// value listed. Mirrored server-side in backend/orders/views.py so admin
// filtering expands the same pairs (DEV-51079).
export const STATUS_FILTER_GROUPS: Record<string, string[]> = {
  fulfillment_declined: ['fulfillment_declined', 'supplier_cancelled'],
}

// Expand a dropdown value to the full list of backend statuses it should
// match. Used by client-side filters (engineer Orders) to include every
// backend value that shares the selected option's label.
export function getStatusFilterMatches(value: string): string[] {
  return STATUS_FILTER_GROUPS[value] ?? [value]
}

// Statuses hidden from user-facing filters. `cancelled` is a vestigial enum
// value never set by any code path — filtering by it would always return
// nothing. Orders in this state still appear in the unfiltered list with
// their real status; they just can't be filtered for directly.
export const HIDDEN_STATUS_VALUES = new Set(['cancelled'])

// Filter dropdown variant: leading "All Status" + the non-hidden statuses.
export const ORDER_STATUS_FILTER_OPTIONS: OrderStatusOption[] = [
  { value: 'all', label: 'All status' },
  ...ORDER_STATUS_OPTIONS.filter((option) => !HIDDEN_STATUS_VALUES.has(option.value)),
]
